/**
 * Ship-To Destinations (SPEC §8.7): every lane of the last twelve months, from
 * the warehouse that shipped to the trade area that received.
 *
 * Everything on the page comes from one payload -- the map, the lane table, the
 * destination panel and the footnote are views of the same rows, so they cannot
 * disagree. Selecting a destination (a dot, a line or a table row) is
 * client-side; the filter bar's origin, business and channel are server-side.
 *
 * **The footnote is not decoration.** Gallons that cannot be drawn -- Canadian
 * branches reached through consolidators, rural ship-tos in no metro -- are
 * stated with their volume, so the map plus the footnote is every gallon in the
 * window. A map that silently drops what it cannot place looks complete and is
 * not.
 */

import { lazy, Suspense, useMemo, useState } from 'react'

import type { CustomReportProps } from '..'
import type { Cell } from '../../lib/types'
import { Card, Empty } from '../shared/Card'
import { count, dataset, type Keyed, text } from '../shared/rows'

import '../report.css'

import type { Destination, Lane, Origin } from './FlowMap'

const FlowMap = lazy(() => import('./FlowMap'))

const NONE = '—'
const LANES_SHOWN = 25
const PANEL_ROWS = 10

function num(value: Cell | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

function whole(n: number | null): string {
  return n === null ? NONE : Math.round(n).toLocaleString('en-US')
}

function money(n: number | null): string {
  return n === null ? NONE : `$${Math.round(n).toLocaleString('en-US')}`
}

function pct(n: number | null): string {
  return n === null ? NONE : `${n.toFixed(1)}%`
}

/** Freight per gallon, to the cent and a tenth -- rates differ in the third place. */
function perGal(n: number | null): string {
  return n === null ? NONE : `$${n.toFixed(3)}/gal`
}

function compactMoney(n: number | null): string {
  if (n === null) return NONE
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`
  return money(n)
}

function compactGal(n: number | null): string {
  if (n === null) return NONE
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`
  return Math.round(n).toLocaleString('en-US')
}

function shortTitle(title: string | null): string {
  if (title === null) return NONE
  return title.replace(/ (Metro|Micro) Area$/, '')
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="std-kpi">
      <div className="std-kpi-label">{label}</div>
      <div className="std-kpi-value">{value}</div>
      {sub !== undefined && <div className="std-kpi-sub">{sub}</div>}
    </div>
  )
}

/** A share bar: the row's gallons as a fraction of its group. */
function Bar({ share }: { share: number | null }) {
  const width = Math.max(0, Math.min(100, share ?? 0))
  return (
    <span className="std-bar" aria-hidden="true">
      <span className="std-bar-fill" style={{ width: `${String(width)}%` }} />
    </span>
  )
}

function ShareTable({
  rows,
  labelKey,
  heading,
}: {
  rows: Keyed[]
  labelKey: string
  heading: string
}) {
  if (rows.length === 0) return <Empty label="Nothing here." />
  return (
    <table className="std-table">
      <thead>
        <tr>
          <th scope="col">{heading}</th>
          <th scope="col" className="cell-number">
            Gallons
          </th>
          <th scope="col" className="std-share-col">
            Share
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, PANEL_ROWS).map((row) => (
          <tr key={text(row[labelKey]) ?? ''}>
            <td>{text(row[labelKey]) ?? NONE}</td>
            <td className="cell-number">{whole(num(row['gallons']))}</td>
            <td className="std-share-col">
              <Bar share={num(row['share_pct'])} />
              <span className="std-share-num">{pct(num(row['share_pct']))}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const END_USERS_SHOWN = 15

/** A rate's distance from the area average, signed, to the tenth of a cent. */
function vsArea(rate: number | null, areaRate: number | null): string {
  if (rate === null || areaRate === null) return NONE
  const diff = rate - areaRate
  const sign = diff > 0 ? '+' : diff < 0 ? '−' : '±'
  return `${sign}$${Math.abs(diff).toFixed(3)}`
}

/**
 * Who in this area carries the freight, and at what rate against their
 * neighbours. Sun Orchard only; an end user with no Sun Orchard gallons is not
 * listed (Natural Brands never bills freight). Ordered by freight billed, so the
 * customers most exposed to the charge come first; the rate column then shows
 * whether that exposure is volume or price.
 */
function FreightByEndUser({ rows, areaRate }: { rows: Keyed[]; areaRate: number | null }) {
  const [showAll, setShowAll] = useState(false)
  const customers = rows
    .filter((row) => (num(row['so_gallons']) ?? 0) > 0)
    .sort(
      (a, b) =>
        (num(b['freight_dollars']) ?? -1) - (num(a['freight_dollars']) ?? -1) ||
        (num(b['so_gallons']) ?? 0) - (num(a['so_gallons']) ?? 0),
    )
  if (customers.length === 0) return null
  const shown = showAll ? customers : customers.slice(0, END_USERS_SHOWN)

  return (
    <section className="std-freight">
      <h3 className="std-section-title">
        Freight by end user, Sun Orchard only · area average {perGal(areaRate)}
      </h3>
      <table className="std-table">
        <thead>
          <tr>
            <th scope="col">End user</th>
            <th scope="col" className="cell-number">
              SO gallons
            </th>
            <th scope="col" className="cell-number">
              Gallons charged
            </th>
            <th scope="col" className="cell-number">
              % charged
            </th>
            <th scope="col" className="cell-number">
              Freight billed
            </th>
            <th scope="col" className="cell-number">
              $/gal charged
            </th>
            <th scope="col" className="cell-number">
              vs area
            </th>
          </tr>
        </thead>
        <tbody>
          {shown.map((row) => {
            const rate = num(row['freight_per_gal'])
            const tone =
              rate === null || areaRate === null
                ? ''
                : rate > areaRate
                  ? 'std-above'
                  : rate < areaRate
                    ? 'std-below'
                    : ''
            return (
              <tr key={text(row['end_user']) ?? ''}>
                <td>{text(row['end_user']) ?? NONE}</td>
                <td className="cell-number">{whole(num(row['so_gallons']))}</td>
                <td className="cell-number">{whole(num(row['freight_gallons']))}</td>
                <td className="cell-number">{pct(num(row['freight_charged_pct']))}</td>
                <td className="cell-number">{money(num(row['freight_dollars']))}</td>
                <td className="cell-number">{perGal(rate)}</td>
                <td className={`cell-number ${tone}`}>{vsArea(rate, areaRate)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {customers.length > END_USERS_SHOWN && (
        <button
          type="button"
          className="std-link"
          onClick={() => {
            setShowAll((current) => !current)
          }}
        >
          {showAll ? 'Show fewer' : `Show all ${String(customers.length)} end users`}
        </button>
      )}
    </section>
  )
}

export function ShipToDestinations({ result }: CustomReportProps) {
  const kpi = dataset(result, 'kpis')[0]
  const originRows = dataset(result, 'origins')
  const laneRows = dataset(result, 'lanes')
  const destRows = dataset(result, 'destinations')
  const destOrigins = dataset(result, 'dest_origins')
  const destEndUsers = dataset(result, 'dest_end_users')
  const destDistributors = dataset(result, 'dest_distributors')
  const destMix = dataset(result, 'dest_mix')
  const destCbsas = dataset(result, 'dest_cbsas')
  const notDrawn = dataset(result, 'not_drawn')

  const [selected, setSelected] = useState<number | null>(null)

  const lanes: Lane[] = useMemo(
    () =>
      laneRows.map((row) => ({
        origin: text(row['origin']) ?? '',
        originLon: num(row['origin_lon']) ?? 0,
        originLat: num(row['origin_lat']) ?? 0,
        tradeAreaCode: count(row['trade_area_code']),
        tradeAreaTitle: shortTitle(text(row['trade_area_title'])),
        hubLon: num(row['hub_lon']) ?? 0,
        hubLat: num(row['hub_lat']) ?? 0,
        gallons: num(row['gallons']) ?? 0,
        fob: num(row['fob']) ?? 0,
        shipTos: count(row['ship_tos']),
        freightDollars: num(row['freight_dollars']),
        freightGallons: num(row['freight_gallons']),
        freightPerGal: num(row['freight_per_gal']),
        freightChargedPct: num(row['freight_charged_pct']),
      })),
    [laneRows],
  )
  const destinations: Destination[] = useMemo(
    () =>
      destRows.map((row) => ({
        tradeAreaCode: count(row['trade_area_code']),
        title: shortTitle(text(row['trade_area_title'])),
        lon: num(row['hub_lon']) ?? 0,
        lat: num(row['hub_lat']) ?? 0,
        gallons: num(row['gallons']) ?? 0,
        freightDollars: num(row['freight_dollars']),
        freightGallons: num(row['freight_gallons']),
        freightPerGal: num(row['freight_per_gal']),
        freightChargedPct: num(row['freight_charged_pct']),
      })),
    [destRows],
  )
  const origins: Origin[] = useMemo(
    () =>
      originRows.map((row) => ({
        name: text(row['origin']) ?? '',
        lon: num(row['origin_lon']) ?? 0,
        lat: num(row['origin_lat']) ?? 0,
        gallons: num(row['gallons']) ?? 0,
      })),
    [originRows],
  )

  // A selection that the filter bar has since removed is no selection.
  const area = destRows.find((row) => count(row['trade_area_code']) === selected)
  const activeSelection = area === undefined ? null : selected
  const forArea = (rows: Keyed[]) =>
    rows.filter((row) => count(row['trade_area_code']) === activeSelection)

  const totalGal = num(kpi?.['total_gal'])
  const drawnGal = num(kpi?.['drawn_gal'])
  const offMap = notDrawn.reduce((sum, row) => sum + (num(row['gallons']) ?? 0), 0)

  // Lane bars are scaled to the heaviest lane, not to 100%: no lane is more than
  // a few percent of the map, and bars of 0-6% would all read as slivers.
  const topLaneShare = Math.max(0, ...laneRows.map((row) => num(row['share_pct']) ?? 0))

  const toggle = (code: number) => {
    setSelected((current) => (current === code ? null : code))
  }

  return (
    <div className="bespoke-report std-page">
      <section className="std-kpis">
        <Kpi
          label="Gallons on the map"
          value={whole(drawnGal)}
          sub={`of ${whole(totalGal)} shipped · ${money(num(kpi?.['drawn_fob']))} FOB`}
        />
        <Kpi
          label="Freight billed (Sun Orchard)"
          value={perGal(num(kpi?.['freight_per_gal']))}
          sub={`${compactMoney(num(kpi?.['freight_dollars']))} on ${compactGal(
            num(kpi?.['freight_gallons']),
          )} gal · ${pct(num(kpi?.['freight_charged_pct']))} of SO gallons charged`}
        />
        <Kpi label="Lanes" value={whole(num(kpi?.['lanes']))} />
        <Kpi
          label="Destinations"
          value={whole(num(kpi?.['destinations']))}
          sub={`${whole(num(kpi?.['ship_tos']))} ship-tos`}
        />
        <Kpi
          label="Heaviest lane"
          value={compactGal(num(kpi?.['top_lane_gal']))}
          sub={`${text(kpi?.['top_lane_origin']) ?? NONE} → ${shortTitle(
            text(kpi?.['top_lane_dest']),
          )}`}
        />
        {originRows.map((row) => (
          <Kpi
            key={text(row['origin']) ?? ''}
            label={`From ${text(row['origin']) ?? NONE}`}
            value={compactGal(num(row['gallons']))}
            sub={`${pct(num(row['share_pct']))} · wh ${text(row['warehouses']) ?? NONE}`}
          />
        ))}
      </section>

      <Card
        title="Shipping lanes, last twelve months"
        subtitle={
          `Shipped gallons ${text(kpi?.['window_from']) ?? ''} to ${
            text(kpi?.['window_to']) ?? ''
          }, all channels, from the warehouse that shipped to the trade area that received ` +
          '(drawn at its hub). Line width, colour and flow speed all scale with volume, on a ' +
          'log scale so small lanes stay visible. Click a dot, a line or a lane row to open ' +
          'a destination. Scroll to zoom, drag to pan. Hawaii is shown inset.'
        }
      >
        {lanes.length === 0 ? (
          <Empty label="No lanes for this selection." />
        ) : (
          <Suspense fallback={<div className="state">Loading map…</div>}>
            <FlowMap
              lanes={lanes}
              destinations={destinations}
              origins={origins}
              selected={activeSelection}
              onSelect={toggle}
            />
          </Suspense>
        )}
        <div className="std-legend">
          <span className="std-legend-ramp" aria-hidden="true" />
          <span>lighter lane → heavier lane (log scale)</span>
        </div>
        {notDrawn.length > 0 && (
          <p className="std-footnote">
            <strong>Not on the map: {whole(offMap)} gal.</strong>{' '}
            {notDrawn.map((row, index) => (
              <span key={text(row['place_kind']) ?? String(index)}>
                {index > 0 ? ' · ' : ''}
                {whole(num(row['gallons']))} gal to {count(row['ship_tos'])}{' '}
                {text(row['label']) ?? ''}
              </span>
            ))}
            . Map plus footnote = {whole(totalGal)} gal.
          </p>
        )}
      </Card>

      {area !== undefined && (
        <Card
          title={text(area['trade_area_title']) ?? 'Destination'}
          subtitle={`${whole(num(area['gallons']))} gal · ${money(num(area['fob']))} FOB · ${whole(
            num(area['ship_tos']),
          )} ship-tos · ${pct(num(area['share_pct']))} of the map · freight ${perGal(
            num(area['freight_per_gal']),
          )} on ${pct(num(area['freight_charged_pct']))} of Sun Orchard gallons`}
          actions={
            <button
              type="button"
              className="std-clear"
              onClick={() => {
                setSelected(null)
              }}
            >
              Clear selection
            </button>
          }
        >
          <div className="std-panel-grid">
            <section>
              <h3 className="std-section-title">Shipped from</h3>
              <ShareTable rows={forArea(destOrigins)} labelKey="origin" heading="Warehouse site" />
            </section>
            <section>
              <h3 className="std-section-title">Channel and business</h3>
              <ShareTable
                rows={forArea(destMix).map((row) => ({
                  ...row,
                  mix_label: `${text(row['dimension']) ?? ''}: ${text(row['label']) ?? ''}`,
                }))}
                labelKey="mix_label"
                heading="Split"
              />
            </section>
            <section>
              <h3 className="std-section-title">Top end users</h3>
              <ShareTable rows={forArea(destEndUsers)} labelKey="end_user" heading="End user" />
            </section>
            <section>
              <h3 className="std-section-title">Top distributors</h3>
              <ShareTable
                rows={forArea(destDistributors)}
                labelKey="distributor_group"
                heading="Distributor"
              />
            </section>
          </div>
          <section className="std-freight">
            <h3 className="std-section-title">Freight billed, Sun Orchard only</h3>
            <table className="std-table">
              <thead>
                <tr>
                  <th scope="col">Shipped from</th>
                  <th scope="col" className="cell-number">
                    SO gallons
                  </th>
                  <th scope="col" className="cell-number">
                    Gallons charged
                  </th>
                  <th scope="col" className="cell-number">
                    % charged
                  </th>
                  <th scope="col" className="cell-number">
                    Freight billed
                  </th>
                  <th scope="col" className="cell-number">
                    $/gal charged
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ...forArea(destOrigins),
                  { ...area, origin: 'All sites', trade_area_code: activeSelection },
                ].map((row) => (
                  <tr key={text(row['origin']) ?? ''}>
                    <td>{text(row['origin']) ?? NONE}</td>
                    <td className="cell-number">{whole(num(row['so_gallons']))}</td>
                    <td className="cell-number">{whole(num(row['freight_gallons']))}</td>
                    <td className="cell-number">{pct(num(row['freight_charged_pct']))}</td>
                    <td className="cell-number">{money(num(row['freight_dollars']))}</td>
                    <td className="cell-number">{perGal(num(row['freight_per_gal']))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <FreightByEndUser
            rows={forArea(destEndUsers)}
            areaRate={num(area['freight_per_gal'])}
            key={activeSelection ?? 'none'}
          />
          <section>
            <h3 className="std-section-title">Metro and micro areas in this destination</h3>
            <table className="std-table">
              <thead>
                <tr>
                  <th scope="col">Metro / micro area</th>
                  <th scope="col">Role</th>
                  <th scope="col" className="cell-number">
                    Ship-tos
                  </th>
                  <th scope="col" className="cell-number">
                    Gallons
                  </th>
                  <th scope="col" className="std-share-col">
                    Share
                  </th>
                </tr>
              </thead>
              <tbody>
                {forArea(destCbsas).map((row) => (
                  <tr key={String(count(row['cbsa_code']))}>
                    <td>{text(row['cbsa_title']) ?? NONE}</td>
                    <td>{text(row['is_hub']) === 'yes' ? 'Hub' : 'Member'}</td>
                    <td className="cell-number">{whole(num(row['ship_tos']))}</td>
                    <td className="cell-number">{whole(num(row['gallons']))}</td>
                    <td className="std-share-col">
                      <Bar share={num(row['share_pct'])} />
                      <span className="std-share-num">{pct(num(row['share_pct']))}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </Card>
      )}

      <Card
        title="Lanes, heaviest first"
        subtitle={`The top ${String(Math.min(LANES_SHOWN, laneRows.length))} of ${String(
          laneRows.length,
        )} lanes. Share is of the gallons on the map. Click a row to open its destination.`}
      >
        {laneRows.length === 0 ? (
          <Empty label="No lanes for this selection." />
        ) : (
          <table className="std-table std-lanes">
            <thead>
              <tr>
                <th scope="col" className="cell-number">
                  #
                </th>
                <th scope="col">From</th>
                <th scope="col">To</th>
                <th scope="col" className="cell-number">
                  Ship-tos
                </th>
                <th scope="col" className="cell-number">
                  Gallons
                </th>
                <th scope="col" className="cell-number">
                  FOB
                </th>
                <th scope="col" className="cell-number">
                  Freight $/gal
                </th>
                <th scope="col" className="cell-number">
                  % charged
                </th>
                <th scope="col" className="std-share-col">
                  Share
                </th>
              </tr>
            </thead>
            <tbody>
              {laneRows.slice(0, LANES_SHOWN).map((row) => {
                const code = count(row['trade_area_code'])
                const isSel = code === activeSelection
                return (
                  <tr
                    key={`${text(row['origin']) ?? ''}|${String(code)}`}
                    className={isSel ? 'std-row std-row-selected' : 'std-row'}
                    onClick={() => {
                      toggle(code)
                    }}
                  >
                    <td className="cell-number">{whole(num(row['rank']))}</td>
                    <td>{text(row['origin']) ?? NONE}</td>
                    <td>
                      <button
                        type="button"
                        className="std-link"
                        onClick={(event) => {
                          event.stopPropagation()
                          toggle(code)
                        }}
                      >
                        {shortTitle(text(row['trade_area_title']))}
                      </button>
                    </td>
                    <td className="cell-number">{whole(num(row['ship_tos']))}</td>
                    <td className="cell-number">{whole(num(row['gallons']))}</td>
                    <td className="cell-number">{money(num(row['fob']))}</td>
                    <td className="cell-number">{perGal(num(row['freight_per_gal']))}</td>
                    <td className="cell-number">{pct(num(row['freight_charged_pct']))}</td>
                    <td className="std-share-col">
                      <Bar
                        share={
                          topLaneShare > 0
                            ? ((num(row['share_pct']) ?? 0) / topLaneShare) * 100
                            : null
                        }
                      />
                      <span className="std-share-num">{pct(num(row['share_pct']))}</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  )
}
